import type {
  AccountCashSetting,
  CashTargetCondition,
} from "../../../api/src/cash-settings/lib";
import { formatCurrency, formatPercent } from "../lib/numbers";

/**
 * The cash settings of an account as the API sends them: `cashSettings` of
 * `GET /v1/accounts?balances=true` and of each account of
 * `GET /v1/rebalances/:id?metrics=true`. The percent fields are typed as
 * numbers but arrive as decimal strings on 0 to 100 (`"5.00"`), as pg returns
 * a `decimal(5, 2)` (`api/src/cash-settings/lib.ts`), so every read here goes
 * through `toNumber`.
 */
export type CashSettingsInput = Omit<
  AccountCashSetting,
  "accountId" | "cashSettingId"
> &
  Partial<Pick<AccountCashSetting, "accountId" | "cashSettingId">>;

/** A number from a number or decimal string; `null` for anything else. */
export function toNumber(
  value: number | string | null | undefined,
): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export type CashSplitSummary = {
  /** Symbol of the security that holds the split share of the target. */
  symbol: string;
  /** Share of the cash target held in `symbol`, on 0 to 1. */
  splitShare: number;
  /** Dollars of the cash target held in `symbol`. */
  splitDollars: number;
  /** Share of the cash target left in the cash sweep, on 0 to 1. */
  sweepShare: number;
  /** Dollars of the cash target left in the cash sweep. */
  sweepDollars: number;
};

export type CashTargetSummary = {
  /** The cash target in dollars. */
  dollars: number;
  /**
   * The cash target as a share of the account value, on 0 to 1. `null` for a
   * dollar target on an account with no value.
   */
  ratio: number | null;
  /** How the target splits between the split security and the sweep. */
  split: CashSplitSummary | null;
};

/**
 * The split percentage of a cash split that has none set, on 0 to 100: the
 * rebalancer's `DEFAULT_CASH_SPLIT_FRACTION_OF_TARGET`
 * (`python/src/rebalance/cash_settings.py`).
 */
const DEFAULT_CASH_SPLIT_PERCENT = 50;

/**
 * The effective cash target of an account, in dollars and as a share of
 * `accountValue`, the one computed from the other the way the rebalancer
 * reads it: a `percent` target is that percent of the account value, an
 * `absolute` target is the dollar amount. `null` when the account has no
 * target: no cash settings, or no value set for the target type (the
 * `target` of `getCashTargetsAbsolute`, `api/src/cash-settings/lib.ts`).
 *
 * The split is the account's own `cashSplitPercent` of the target in
 * `cashSplitSymbol`, the sweep the rest (section 4.1 of
 * `doc-cash-split-01a05d5f47df200b`), by the rules of the rebalancer's
 * `resolve_cash_split` (`python/src/rebalance/cash_settings.py`):
 * - a split symbol with no split percentage divides the target 50/50
 *   (`DEFAULT_CASH_SPLIT_FRACTION_OF_TARGET`);
 * - a target of zero dollars has no split, the target itself still shows;
 * - a split percentage with no split symbol has no split.
 */
export function summarizeCashTarget(
  cashSettings: CashSettingsInput | null | undefined,
  accountValue: number,
): CashTargetSummary | null {
  if (cashSettings == null) {
    return null;
  }
  let dollars: number;
  let ratio: number | null;
  if (cashSettings.cashTargetType === "percent") {
    const percent = toNumber(cashSettings.cashTargetPercent);
    if (percent === null) {
      return null;
    }
    ratio = percent / 100;
    dollars = ratio * accountValue;
  } else {
    const absolute = toNumber(cashSettings.cashTargetAbsolute);
    if (absolute === null) {
      return null;
    }
    dollars = absolute;
    ratio = accountValue > 0 ? absolute / accountValue : null;
  }

  const symbol = cashSettings.cashSplitSymbol?.trim();
  const splitPercent =
    toNumber(cashSettings.cashSplitPercent) ?? DEFAULT_CASH_SPLIT_PERCENT;
  const split: CashSplitSummary | null =
    !symbol || dollars === 0
      ? null
      : {
          symbol,
          splitShare: splitPercent / 100,
          splitDollars: (dollars * splitPercent) / 100,
          sweepShare: (100 - splitPercent) / 100,
          sweepDollars: (dollars * (100 - splitPercent)) / 100,
        };

  return { dollars, ratio, split };
}

/** A share on 0 to 1 as a percent with up to two decimals; `—` for `null`. */
export function formatShare(ratio: number | null): string {
  return formatPercent(ratio ?? Number.NaN, 2);
}

/** One cash trigger: its dollar amount, its percent, or both joined by its condition. */
function formatTrigger(
  absolute: number | string | null,
  percent: number | string | null,
  condition: CashTargetCondition,
): string | null {
  const dollars = toNumber(absolute);
  const pct = toNumber(percent);
  if (dollars === null && pct === null) {
    return null;
  }
  if (pct === null) {
    return formatCurrency(dollars);
  }
  if (dollars === null) {
    return formatShare(pct / 100);
  }
  return `${formatCurrency(dollars)} ${condition} ${formatShare(pct / 100)}`;
}

/**
 * The minimum and maximum cash triggers as configured, such as
 * `$5,000 / $20,000` or `2% / 8%`, `—` for a side with no trigger. `null`
 * when neither side has one, or there are no cash settings.
 */
export function formatCashMinMax(
  cashSettings: CashSettingsInput | null | undefined,
): string | null {
  if (cashSettings == null) {
    return null;
  }
  const min = formatTrigger(
    cashSettings.cashTargetMinAbsolute,
    cashSettings.cashTargetMinPercent,
    cashSettings.cashTargetMinCondition,
  );
  const max = formatTrigger(
    cashSettings.cashTargetMaxAbsolute,
    cashSettings.cashTargetMaxPercent,
    cashSettings.cashTargetMaxCondition,
  );
  if (min === null && max === null) {
    return null;
  }
  return `${min ?? "—"} / ${max ?? "—"}`;
}
